"use client";

import { useState } from "react";
import Button from "@/app/components/Button";
import { useConfirm } from "@/app/components/ConfirmDialog";

const WORLD = "Baunheim";
const TEXT_MAX = 2000;

type Kind = "feedback" | "fix" | "rework";

interface Target {
  slug: string;
  name: string;
  /** 同行者は持ち主の PC のスラッグ */
  owner?: string;
}

const KINDS: { kind: Kind; label: string; lead: string; placeholder: string; done: string }[] = [
  {
    kind: "feedback",
    label: "FB 報告",
    lead: "画面の不具合や、遊んでいて気づいたことを書いて送ります。",
    placeholder: "例：ログの画面で戦闘のタブを開くと、文字が重なって読めない",
    done: "FB 報告を受け付けました。",
  },
  {
    kind: "fix",
    label: "修正依頼",
    lead: "物語やキャラの中身で直してほしい所を書いて送ります。",
    placeholder: "例：2 日目の場面で、持っていない剣を使っている",
    done: "修正依頼を受け付けました。",
  },
  {
    kind: "rework",
    label: "手直し依頼",
    lead: "キャラの設定（名前・外見・経歴・持ち物・スキル・能力）で直したい所を書いて送ります。これまでの物語と食い違う所は、物語の記録も合わせて直します。ほかのプレイヤーのキャラに関わる所は、開発者の許可を待ちます。",
    placeholder: "例：髪の色を黒から銀に替えたい",
    done: "手直しの依頼を受け付けました。結果はお知らせに届きます。",
  },
];

/**
 * 開発へメッセージの画面。FB 報告・修正依頼・手直し依頼の 3 種を選んで 1 件ずつ送る。
 * 手直し依頼は、遊んでいるキャラかその同行者を選ぶ
 */
export default function PlayMessage({ region, slug, targets, onBack }: { region: string; slug: string; targets: Target[]; onBack: () => void }) {
  const [kind, setKind] = useState<Kind>("feedback");
  const [target, setTarget] = useState(slug);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState<{ id: string; done: string } | null>(null);
  const [ask, confirmDialog] = useConfirm();

  const spec = KINDS.find((one) => one.kind === kind) ?? KINDS[0];
  const chosen = targets.find((one) => one.slug === target) ?? targets[0];

  const send = async () => {
    if (sending || text.trim() === "") return;
    const head = kind === "rework" ? `${chosen?.name ?? ""} の手直しを依頼します。` : `この内容で${spec.label}を送ります。`;
    if (!(await ask(`${head}\n送った後は取り消せません。`, { ok: "送る" }))) return;
    setSending(true);
    setMessage("");
    try {
      const [url, body] =
        kind === "rework"
          ? ["/api/character/rework", { world: WORLD, region, slug, text, companion: chosen?.owner ? chosen.slug : null }]
          : [`/api/play/${kind === "fix" ? "fix_request" : "feedback"}`, { world: WORLD, region, slug, text }];
      const response = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error ?? "送れません");
      setSent({ id: result.id as string, done: spec.done });
      setText("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "送れません");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* ヘッダ。題と、選んでいる種類の説明と、その他へ戻るボタン */}
      <div className="pl-title">
        <h2>開発へメッセージ</h2>
        <p className="pl-title-lead">{spec.lead}</p>
        <button type="button" className="pl-back" onClick={onBack}>
          戻る
        </button>
      </div>
      {sent ? (
        <div className="pl-empty">
          <strong>{sent.done}</strong>
          受付番号 {sent.id}
          <div className="pl-fix-actions">
            <Button onClick={() => setSent(null)}>続けて送る</Button>
          </div>
        </div>
      ) : (
        <>
          <div className="pl-sheet-row">
            <select
              className="pl-select"
              value={kind}
              onChange={(event) => {
                setKind(event.target.value as Kind);
                setMessage("");
              }}
              disabled={sending}
              aria-label="メッセージの種類"
            >
              {KINDS.map((one) => (
                <option key={one.kind} value={one.kind}>
                  {one.label}
                </option>
              ))}
            </select>
          </div>
          {kind === "rework" && targets.length > 1 ? (
            <div className="pl-sheet-row">
              <select className="pl-select" value={target} onChange={(event) => setTarget(event.target.value)} disabled={sending} aria-label="手直しする相手">
                {targets.map((one) => (
                  <option key={one.slug} value={one.slug}>
                    {one.name}
                    {one.owner ? "（同行者）" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <textarea
            className="pl-textarea pl-fix-text"
            maxLength={TEXT_MAX}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={spec.placeholder}
            disabled={sending}
          />
          <div className="pl-sheet-row">
            <span className="pl-sheet-save">
              {text.length} / {TEXT_MAX}
            </span>
          </div>
          <div className="pl-fix-actions">
            <Button variant="primary" onClick={send} disabled={sending || text.trim() === ""}>
              {sending ? "送っています" : "送る"}
            </Button>
          </div>
          {message ? <p className="pl-message">{message}</p> : null}
        </>
      )}
      {confirmDialog}
    </div>
  );
}
